import React, { useState } from "react"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"

export default function AffiliatesApply() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    region: "",
    message: ""
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!formData.name || !formData.email) {
      toast.error("Please fill in all required fields")
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address")
      return
    }

    setIsSubmitting(true)

    try {
      // Store in database
      await lumi.entities.Applications.create({
        ...formData,
        role_requested: "affiliate",
        createdAt: new Date().toISOString(),
        status: "pending"
      })

      // Send email notification
      await lumi.tools.email.send({
        to: "hello@nursinghive.com",
        subject: "New Affiliate Application",
        html: `
          <h2>New Affiliate Application</h2>
          <p><strong>Name:</strong> ${formData.name}</p>
          <p><strong>Email:</strong> ${formData.email}</p>
          <p><strong>Region:</strong> ${formData.region || "N/A"}</p>
          <p><strong>About Network/Referrals:</strong></p>
          <p>${formData.message || "N/A"}</p>
        `,
        fromName: "Nursing Hive Affiliates"
      })

      setSubmitted(true)
      toast.success("Application submitted successfully!")
    } catch (error) {
      console.error("Error submitting application:", error)
      toast.error("Failed to submit application. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-white pt-32 pb-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Thank you. We'll contact you soon after review.
          </h1>
          <p className="text-lg text-gray-600 mb-12">
            Your application has been received. We'll review your details and contact you soon.
          </p>

          <div className="bg-gray-50 rounded-xl p-8 text-left">
            <h2 className="text-xl font-bold text-gray-900 mb-4">What happens next?</h2>
            <ol className="space-y-3 text-gray-700">
              <li className="flex gap-3">
                <span className="font-bold text-teal-600">1.</span>
                <span>We review your details (3–5 business days)</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-teal-600">2.</span>
                <span>Verification & onboarding pack</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-teal-600">3.</span>
                <span>You'll receive an invite link if approved</span>
              </li>
            </ol>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white pt-32 pb-20 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Apply for Affiliate Access
          </h1>
          <p className="text-xl text-gray-600">
            Partner with Nursing Hive as an approved affiliate.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
              Your Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Full name"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label htmlFor="region" className="block text-sm font-semibold text-gray-700 mb-2">
              Region / Country
            </label>
            <input
              type="text"
              id="region"
              name="region"
              value={formData.region}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="e.g., Pakistan, India, GCC"
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
              Tell us about your network or referrals
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Describe your network, audience, or referral capabilities..."
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-teal-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Submitting..." : "Submit Application"}
          </button>
        </form>

        {/* What Happens Next */}
        <div className="mt-12 bg-gray-50 rounded-xl p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">What happens next?</h2>
          <ol className="space-y-3 text-gray-700">
            <li className="flex gap-3">
              <span className="font-bold text-teal-600">1.</span>
              <span>We review your details (3–5 business days)</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-teal-600">2.</span>
              <span>Verification & onboarding pack</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-teal-600">3.</span>
              <span>You'll receive an invite link if approved</span>
            </li>
          </ol>
        </div>
      </div>
    </div>
  )
}
